// =============================================================================
// sessionLock — no remembered sign-ins, and an idle lock (JJ, 28 Sep 2026).
//
// PLAIN ENGLISH. The till used to remember who was signed in: reopen or refresh
// the app and it went straight back in as the last person, no PIN asked. On a
// staff member's own phone that meant the app stayed open to them — at home,
// after their shift, after they'd left — for as long as the browser tab lived.
// And switching a PIN off in the Command Centre did nothing to a session that
// was already open, because that check only runs at the PIN pad.
//
// Now, on every real shop host:
//   1. Every fresh open of the app (first load, refresh, a tab the phone threw
//      away and reloaded) starts at the PIN pad. Nothing about who was signed in
//      is kept between page loads, and the copy older builds saved is wiped on
//      the first load of this one.
//   2. After IDLE_LOCK_MS with no taps, the app locks itself back to the PIN pad
//      — whether the screen was on, asleep, or the app was in the background.
//
// Local preview hosts (localhost, 127.0.0.1, LAN addresses) keep the old
// refresh-survives behaviour so the QA harness can seed a session without
// tapping a real PIN, and accept ?idlelock=<seconds> so the lock can be tested
// without waiting ten minutes. Neither applies on a shop host.
//
// No React in here: the rules and the idle watcher are pinned by
// sessionLock.test.mjs (the watcher takes the window, document, clock and
// timers as arguments so the test can drive it).
// =============================================================================

export const IDLE_LOCK_MS = 10 * 60 * 1000;

// Every sessionStorage key an app build has used to carry a sign-in across a
// reload: the front-of-house app (who, their role, which screen, the Club Card
// scanner's tokens) and the kitchen app (which screen, who).
export const SAVED_SESSION_KEYS = [
  "rolled.view", "rolled.staff", "rolled.staffRole", "rolled.cc.session.v1",
  "rolled.kitchen.view", "rolled.kitchen.staff",
];

// "Is this a dev preview rather than a till?" — the exact test foh.jsx has
// always used for its preview-PIN shortcut: localhost, 127.0.0.1, or a private
// LAN address. Every shop host (*.eatrolled.com) is false.
export function isPreviewHost(hostname) {
  const h = String(hostname || "").toLowerCase();
  return h === "localhost" || h === "127.0.0.1" || h.startsWith("192.168.") || h.startsWith("10.") || h.startsWith("172.");
}

// May this page load pick a sign-in back up from sessionStorage (or save one)?
// Only on a preview host.
export function sessionRestoreAllowed(hostname) {
  return isPreviewHost(hostname);
}

// Remove every saved-sign-in key from `storage` (sessionStorage in the app).
// Returns how many were there. Never throws — a blocked or missing storage
// simply means there was nothing to wipe.
export function wipeSavedSession(storage) {
  if (!storage) return 0;
  let n = 0;
  for (const k of SAVED_SESSION_KEYS) {
    try {
      if (storage.getItem(k) != null) { storage.removeItem(k); n++; }
    } catch (e) { /* blocked storage: nothing saved to wipe */ }
  }
  return n;
}

// The idle window for this page. A shop host always gets IDLE_LOCK_MS; a
// preview host may shorten it with ?idlelock=<seconds> (5 s minimum) to test
// the lock. `query` is the raw ?idlelock= value, or null.
export function idleLockMsFor({ hostname = "", query = null } = {}) {
  if (isPreviewHost(hostname) && query != null && /^\d+$/.test(String(query))) {
    const s = Number(query);
    if (s >= 5) return s * 1000;
  }
  return IDLE_LOCK_MS;
}

// Has the session gone idle? Exactly idleMs without use counts as idle. A
// last-use time AFTER now (the device clock was stepped back) is not idle —
// the caller treats that moment as fresh use rather than locking someone out
// mid-task. Anything unreadable is idle: fail towards the PIN pad.
export function idleExpired(lastUseAt, now, idleMs) {
  const last = Number(lastUseAt), t = Number(now), w = Number(idleMs);
  if (!Number.isFinite(last) || !Number.isFinite(t) || !(w > 0)) return true;
  if (t < last) return false;
  return t - last >= w;
}

// The events that count as someone using the app.
export const USE_EVENTS = ["pointerdown", "touchstart", "keydown", "wheel"];

// Watch for `idleMs` without use, then call onLock (once). Returns stop().
//
//  - Timed on the wall clock (Date.now), NOT performance.now: a phone asleep in
//    a pocket may not advance its monotonic clock, and that is exactly the case
//    this lock is for.
//  - Checked on a timer AND the moment the page is visible again (screen woken,
//    tab reopened, page restored from the back/forward cache), so a phone picked
//    up an hour later shows the PIN pad, not the last screen.
//  - A tap that arrives AFTER the window has already passed (a kiosk WebView
//    that froze its timers and fired no wake event) locks instead of quietly
//    extending the session, and the click that follows it is swallowed so it
//    can't land on the PIN pad that has just replaced the screen.
//  - If onLock throws, the error is reported and the next check tries again: a
//    half-finished lock must never quietly leave the app open.
export function startIdleWatch({
  win, doc, idleMs, onLock,
  now = () => Date.now(),
  setIntervalFn = setInterval, clearIntervalFn = clearInterval, setTimeoutFn = setTimeout,
  onError = (e) => { try { console.error("[session-lock] lock failed", e); } catch (_) {} },
}) {
  let last = now(), done = false;
  const tryLock = () => {
    if (done) return;
    done = true;
    try { onLock(); } catch (e) { done = false; onError(e); }
  };
  const check = () => {
    if (done) return;
    const t = now();
    if (t < last) { last = t; return; } // clock stepped back — count it as use, don't lock mid-task
    if (idleExpired(last, t, idleMs)) tryLock();
  };
  const swallowClick = (ev) => { try { ev.stopPropagation(); ev.preventDefault(); } catch (_) {} };
  const onUse = () => {
    if (done) return;
    const t = now();
    if (t >= last && idleExpired(last, t, idleMs)) {
      // Expired before this tap: lock, and never extend — even if the lock failed,
      // so the next check tries again.
      tryLock();
      if (done) {
        win.addEventListener("click", swallowClick, { capture: true, once: true });
        setTimeoutFn(() => { try { win.removeEventListener("click", swallowClick, { capture: true }); } catch (_) {} }, 800);
      }
      return;
    }
    last = t;
  };
  const onShow = () => { if (!doc.hidden) check(); };
  USE_EVENTS.forEach((ev) => win.addEventListener(ev, onUse, { capture: true, passive: true }));
  doc.addEventListener("visibilitychange", onShow);
  win.addEventListener("pageshow", onShow);
  win.addEventListener("focus", onShow);
  const iv = setIntervalFn(check, Math.max(1000, Math.min(15000, Math.floor(idleMs / 4))));
  return function stop() {
    USE_EVENTS.forEach((ev) => win.removeEventListener(ev, onUse, { capture: true }));
    doc.removeEventListener("visibilitychange", onShow);
    win.removeEventListener("pageshow", onShow);
    win.removeEventListener("focus", onShow);
    clearIntervalFn(iv);
  };
}
